import type {ReactNode} from 'react'
import {useNavigate} from 'react-router'
import {Tooltip, TooltipAnchor, TooltipArrow, TooltipProvider} from '@ariakit/react/tooltip'
import {faSignInAlt, faTimes} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import type {LinksT} from '@freckle/educator-entities/ts/common/helpers/ajax'
import {
  validateGrade,
  gradeIntAsString,
  gradeStringToInt
} from '@freckle/educator-entities/ts/common/helpers/common-helper'
import {Subjects} from '@freckle/educator-entities/ts/common/helpers/subjects'
import type {TeacherRole} from '@freckle/educator-entities/ts/common/helpers/teacher-role'
import {
  numberOfPremiumSubjects,
  hasPremiumSubject
} from '@freckle/educator-entities/ts/common/logic/premium-access'
import type {PremiumLicensesAttrs} from '@freckle/educator-entities/ts/common/models/premium-licenses'
import type {IdentityProviderT} from '@freckle/educator-entities/ts/roster/identity-provider'
import {getIdpLabel} from '@freckle/educator-entities/ts/roster/identity-provider'
import {Badge} from '@freckle/educator-materials/ts/components/badge'
import {Button} from '@freckle/educator-materials/ts/components/button'
import {Flex} from '@freckle/educator-materials/ts/components/flex'
import {Link} from '@freckle/educator-materials/ts/components/link'
import {OverlayTrigger} from '@freckle/educator-materials/ts/components/overlay-trigger'
import {Popover} from '@freckle/educator-materials/ts/components/popover'
import {PremiumPopover} from '@freckle/educator-materials/ts/components/popover/premium-popover'
import {
  Table,
  cellFromRow,
  aligned,
  disableSortBy,
  idAccessorFromProp,
  columnWhen
} from '@freckle/educator-materials/ts/components/table'
import {Text} from '@freckle/educator-materials/ts/components/typography'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {SafeTrans} from '@freckle/educator-materials/ts/helpers/translate/component'
import {Grades, Role} from '@freckle/school/ts/common/components/table/cell'
import type {SummaryT} from '@freckle/school/ts/common/models/summary'
import type {DashboardQueryParamsT} from '@freckle/school/ts/common/routers/routes'
import {
  Routes,
  STUDENT_FILTER_QUERY_PARAM_TAG,
  TEACHER_FILTER_QUERY_PARAM_TAG
} from '@freckle/school/ts/common/routers/routes'
import {ApiControlledTabContainer} from '@freckle/school/ts/manage-teachers/components/api-controlled-table'
import type {TableParamsT} from '@freckle/school/ts/manage-teachers/components/api-controlled-table'
import {FilterInput} from '@freckle/school/ts/manage-teachers/components/filter-input'
import {RoleDropdown} from '@freckle/school/ts/manage-teachers/components/role-dropdown'
import {SourceDropdown} from '@freckle/school/ts/manage-teachers/components/source-dropdown'
import type {TeacherTabFilteringT} from '@freckle/school/ts/manage-teachers/components/teacher-tab'
import type {
  StudentSimplifiedT,
  SchoolSimplifiedT
} from '@freckle/school/ts/manage-teachers/models/teacher-table-row'
import {getInitialFilters} from './utils'
import {
  actionTooltip,
  actionColumn,
  popoverTextStyle,
  freePopoverStyle,
  removeBtn,
  headerVerticalAlign
} from './teacher-table.module.scss'

type ActionColumnT = {
  showImpersonateModal: () => void
  showRemoveModal: () => void
}

/* eslint-disable react/no-unused-prop-types */
export type TableRowT = {
  id: number
  name: string
  email: string
  grades: Array<number>
  school: SchoolSimplifiedT
  students: Array<StudentSimplifiedT>
  actions: ActionColumnT
  role: TeacherRole | undefined | null
  source: IdentityProviderT | undefined | null
  premiumLicenses: PremiumLicensesAttrs | undefined | null
}

type Props = {
  currentSchoolIds: Array<number> | undefined | null
  tableParams: TableParamsT<TeacherTabFilteringT>
  filters: ReactNode
  fetchTableRows: (newTableParams: TableParamsT<TeacherTabFilteringT>) => Promise<{
    links: LinksT
    rows: Array<TableRowT>
    totalCount: number
  }>
  onUpdate: (newTableParams: TableParamsT<TeacherTabFilteringT>) => void
  hasRGPIdp: boolean
  summary: SummaryT
}

export default function TeacherTable(props: Props) {
  const {t} = useSafeTranslation()
  const {currentSchoolIds, tableParams, fetchTableRows, filters, onUpdate, hasRGPIdp, summary} =
    props

  const routerHistory = useNavigate()

  const initialFilters = getInitialFilters(tableParams)

  // Preserve pagination limit
  const paginationLimit = tableParams.limit !== null ? {limit: tableParams.limit} : {}

  const changeRoute = (additionalFilters: DashboardQueryParamsT['additionalFilters']) =>
    routerHistory(
      Routes.dashboard({
        schoolIds: currentSchoolIds,
        activeTab: 'teacher-tab',
        additionalFilters,
        ...paginationLimit
      })
    )

  return (
    <ApiControlledTabContainer
      name="teacher"
      tableParams={tableParams}
      routerHistory={routerHistory}
      fetchRows={fetchTableRows}
      filters={filters}
      onUpdate={onUpdate}
      render={({paginationPanel: PaginationPanel}, rows) => (
        <>
          <Table
            data={rows}
            dataTest="teacher-table"
            columns={[
              {
                Header: (
                  <>
                    <Text style="label-1" htmlElement="label">
                      {t('MANAGE_TEACHERS_TEACHER_TAB_TEACHER_TABLE_TEACHER_NAME_HEADER')}
                    </Text>
                    <FilterInput
                      dataTest="search-input"
                      initValue={initialFilters.teacherName}
                      routeFn={teacherName =>
                        changeRoute({
                          tag: TEACHER_FILTER_QUERY_PARAM_TAG,
                          teacherName
                        })
                      }
                    />
                  </>
                ),
                ...idAccessorFromProp<TableRowT>('name'),
                widthPx: 130
              },
              {
                Header: (
                  <>
                    <Text style="label-1" htmlElement="label">
                      {t('MANAGE_TEACHERS_TEACHER_TAB_TEACHER_TABLE_EMAIL_HEADER')}
                    </Text>
                    <FilterInput
                      initValue={initialFilters.email}
                      routeFn={email =>
                        changeRoute({
                          tag: TEACHER_FILTER_QUERY_PARAM_TAG,
                          email
                        })
                      }
                    />
                  </>
                ),
                ...idAccessorFromProp<TableRowT>('email'),
                widthPx: 215
              },
              {
                Header: (
                  <RoleDropdown
                    initRole={initialFilters.role}
                    routeFn={role =>
                      changeRoute({
                        tag: TEACHER_FILTER_QUERY_PARAM_TAG,
                        role
                      })
                    }
                  />
                ),
                Cell: cellFromRow(({role}: TableRowT) => <Role role={role} />),
                ...idAccessorFromProp<TableRowT>('role'),
                widthPx: 90
              },
              {
                Header: (
                  <>
                    <Text style="label-1" htmlElement="label">
                      {t('MANAGE_TEACHERS_TEACHER_TAB_TEACHER_TABLE_GRADES_HEADER')}
                    </Text>
                    <FilterInput
                      initValue={initialFilters.studentGrade}
                      routeFn={studentGrade =>
                        changeRoute({
                          tag: TEACHER_FILTER_QUERY_PARAM_TAG,
                          studentGrade
                        })
                      }
                      parseInput={s => gradeIntAsString(gradeStringToInt(s))}
                      validateInput={validateGrade}
                    />
                  </>
                ),
                Cell: cellFromRow(({grades}: TableRowT) => <Grades grades={grades} />),
                ...idAccessorFromProp<TableRowT>('grades'),
                widthPx: 56
              },
              {
                Header: (
                  <Text style="label-1" className={headerVerticalAlign}>
                    {t('MANAGE_TEACHERS_TEACHER_TAB_TEACHER_TABLE_STUDENTS_HEADER')}
                  </Text>
                ),
                Cell: cellFromRow((row: TableRowT) => (
                  <Students row={row} currentSchoolIds={currentSchoolIds} />
                )),
                ...idAccessorFromProp<TableRowT>('students'),
                widthPx: 80
              },
              {
                Header: (
                  <SourceDropdown
                    id="dashboard-teachers-source-selector"
                    initSources={initialFilters.sources}
                    summary={{tag: 'for-teacher', ...summary}}
                    routeFn={identityProviders =>
                      changeRoute({
                        tag: TEACHER_FILTER_QUERY_PARAM_TAG,
                        identityProviders
                      })
                    }
                    labelText={t('MANAGE_TEACHERS_TEACHER_TAB_TEACHER_TABLE_ROSTERED_BY_HEADER')}
                  />
                ),
                Cell: cellFromRow(({source: idp}: TableRowT) => <>{getIdpLabel(t, idp)}</>),
                ...idAccessorFromProp<TableRowT>('source'),
                widthPx: 160
              },
              {
                Header: (
                  <>
                    <Text style="label-1" htmlElement="label">
                      {t('MANAGE_TEACHERS_TEACHER_TAB_TEACHER_TABLE_SCHOOL_HEADER')}
                    </Text>
                    <FilterInput
                      initValue={initialFilters.schoolName}
                      routeFn={schoolName =>
                        changeRoute({
                          tag: TEACHER_FILTER_QUERY_PARAM_TAG,
                          schoolName
                        })
                      }
                    />
                  </>
                ),
                Cell: cellFromRow(({school: {name}}: TableRowT) => <>{name}</>),
                ...idAccessorFromProp<TableRowT>('school'),
                widthPx: 150
              },
              ...columnWhen(hasRGPIdp, {
                Header: (
                  <Text style="label-1" className={headerVerticalAlign}>
                    {t('MANAGE_TEACHERS_TEACHER_TAB_TEACHER_TABLE_PREMIUM_ACCESS_HEADER')}
                  </Text>
                ),
                Cell: cellFromRow((row: TableRowT) => <PremiumLicenses row={row} />),
                widthPx: 100,
                ...idAccessorFromProp<TableRowT>('premiumLicenses'),
                ...aligned('center')
              }),
              {
                Header: (
                  <Text style="label-1" className={headerVerticalAlign}>
                    <span className={actionColumn}>
                      {t('MANAGE_TEACHERS_TEACHER_TAB_TEACHER_TABLE_ACTIONS_HEADER')}
                    </span>
                  </Text>
                ),
                Cell: cellFromRow((row: TableRowT) => <Actions row={row} />),
                ...idAccessorFromProp<TableRowT>('actions'),
                widthPx: 80,
                ...aligned('right')
              }
            ].map(c => ({...aligned('left'), ...c, disableSortBy}))}
          />
          <PaginationPanel components={{sizePerPageDropdown: <></>, pageList: <></>}} />
        </>
      )}
    />
  )
}

const Students = ({
  row: {students, id, name},
  currentSchoolIds
}: {
  row: TableRowT
  currentSchoolIds: Array<number> | null | undefined
}) => {
  const queryParams = {
    schoolIds: currentSchoolIds,
    activeTab: 'student-tab' as const,
    additionalFilters: {
      tag: STUDENT_FILTER_QUERY_PARAM_TAG,
      teacherId: id,
      teacherName: name
    }
  }
  return (
    <Link reactRouter href={Routes.dashboard(queryParams)}>
      {students.length}
    </Link>
  )
}

const PopoverContent = ({
  teacherName,
  premiumLicenses
}: {
  teacherName: string
  premiumLicenses: PremiumLicensesAttrs
}) => {
  const subjects = Subjects.all().filter(subject => hasPremiumSubject(premiumLicenses, subject))

  switch (subjects.length) {
    case 1:
      return (
        <Text style="body-2" className={popoverTextStyle}>
          <SafeTrans
            i18nKey="MANAGE_TEACHERS_TEACHER_TAB_TEACHER_TABLE_PREMIUM_ACCESS_ONE_SUBJECT_POPOVER_TEXT"
            // Safe by length check in `case`
            values={{teacherName, subject: subjects[0]!}}
          />
        </Text>
      )
    case 2:
      return (
        <Text style="body-2" className={popoverTextStyle}>
          <SafeTrans
            i18nKey="MANAGE_TEACHERS_TEACHER_TAB_TEACHER_TABLE_PREMIUM_ACCESS_TWO_SUBJECT_POPOVER_TEXT"
            // Safe by length check in `case`
            values={{teacherName, subject1: subjects[0]!, subject2: subjects[1]!}}
          />
        </Text>
      )
    case 3:
      return (
        <Text style="body-2" className={popoverTextStyle}>
          <SafeTrans
            i18nKey="MANAGE_TEACHERS_TEACHER_TAB_TEACHER_TABLE_PREMIUM_ACCESS_THREE_SUBJECT_POPOVER_TEXT"
            values={{
              teacherName,
              // Safe by length check in `case`
              subject1: subjects[0]!,
              subject2: subjects[1]!,
              subject3: subjects[2]!
            }}
          />
        </Text>
      )
    case 4:
      return (
        <Text style="body-2" className={popoverTextStyle}>
          <SafeTrans
            i18nKey="MANAGE_TEACHERS_TEACHER_TAB_TEACHER_TABLE_PREMIUM_ACCESS_FOUR_SUBJECT_POPOVER_TEXT"
            values={{
              teacherName,
              // Safe by length check in `case`
              subject1: subjects[0]!,
              subject2: subjects[1]!,
              subject3: subjects[2]!,
              subject4: subjects[3]!
            }}
          />
        </Text>
      )
    default:
      return <></>
  }
}

const PremiumLicenses = ({row: {premiumLicenses, id, name}}: {row: TableRowT}) => {
  const {t} = useSafeTranslation()
  if (
    premiumLicenses === null ||
    premiumLicenses === undefined ||
    numberOfPremiumSubjects(premiumLicenses) === 0
  ) {
    return (
      <OverlayTrigger
        placement="top"
        trigger={['hover', 'focus', 'click']}
        overlay={
          <Popover
            id={`free-access-popover-${id}`}
            title={t('MANAGE_TEACHERS_TEACHER_TAB_TEACHER_TABLE_FREE_ACCESS_TEXT')}
            className={freePopoverStyle}
          >
            <Text style="body-2" className={popoverTextStyle}>
              <SafeTrans
                i18nKey="MANAGE_TEACHERS_TEACHER_TAB_TEACHER_TABLE_FREE_ACCESS_POPOVER_TEXT"
                values={{teacherName: name}}
              />
            </Text>
            <Text style="body-2" className={popoverTextStyle}>
              <SafeTrans i18nKey="MANAGE_TEACHERS_TEACHER_TAB_TEACHER_TABLE_MANAGE_LICENSES_TEXT" />
            </Text>
          </Popover>
        }
      >
        <div>
          <Badge style="default">
            {t('MANAGE_TEACHERS_TEACHER_TAB_TEACHER_TABLE_FREE_ACCESS_TEXT')}
          </Badge>
        </div>
      </OverlayTrigger>
    )
  }

  return (
    <OverlayTrigger
      placement="top"
      trigger={['hover', 'focus', 'click']}
      overlay={
        <PremiumPopover
          id={`premium-access-popover-${id}`}
          title={t('MANAGE_TEACHERS_TEACHER_TAB_TEACHER_TABLE_PREMIUM_POPOVER_TITLE')}
        >
          <PopoverContent teacherName={name} premiumLicenses={premiumLicenses} />
          <Text style="body-2" className={popoverTextStyle}>
            <SafeTrans i18nKey="MANAGE_TEACHERS_TEACHER_TAB_TEACHER_TABLE_MANAGE_LICENSES_TEXT" />
          </Text>
        </PremiumPopover>
      }
    >
      <div>
        <Badge style="premium">
          {t('MANAGE_TEACHERS_TEACHER_TAB_TEACHER_TABLE_PREMIUM_ACCESS_TEXT')}
        </Badge>
      </div>
    </OverlayTrigger>
  )
}

const Actions = ({row: {id, actions}}: {row: TableRowT}) => {
  const {t} = useSafeTranslation()
  return (
    <Flex>
      <TooltipProvider placement="top">
        <TooltipAnchor>
          <Button
            style="link"
            onClick={actions.showImpersonateModal}
            dataTest="impersonate-teacher-btn"
          >
            <FontAwesomeIcon icon={faSignInAlt} size="lg" />
          </Button>
        </TooltipAnchor>
        <Tooltip id={`impersonate-tooltip-${id}`} className={actionTooltip}>
          {t('MANAGE_TEACHERS_TEACHER_TAB_TEACHER_TABLE_LOGIN_TO_ACCOUNT')}
          <TooltipArrow />
        </Tooltip>
      </TooltipProvider>

      <TooltipProvider placement="top">
        <TooltipAnchor>
          <Button
            style="link"
            onClick={actions.showRemoveModal}
            dataTest="remote-teacher-btn"
            className={removeBtn}
          >
            <FontAwesomeIcon icon={faTimes} size="lg" />
          </Button>
        </TooltipAnchor>
        <Tooltip id={`remove-tooltip-${id}`} className={actionTooltip}>
          {t('MANAGE_TEACHERS_TEACHER_TAB_TEACHER_TABLE_REMOVE_TOOLTIP')}
          <TooltipArrow />
        </Tooltip>
      </TooltipProvider>
    </Flex>
  )
}
